import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Plane, Hotel, ShieldCheck, Headphones, Star, ArrowRight, ChevronDown,
  MapPin, Calendar, Sparkles, Heart, Quote, Instagram, ArrowUpRight, CheckCircle2,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Nav from "@/components/Nav";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { PACKAGES } from "@/data/content";
import { waLink, waPackageMessage, formatPrice, TIKTOK_URL, LOGO_URL, WA_DISPLAY } from "@/lib/wa";

/* ---------------- Hero ---------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden isolate">
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.unsplash.com/photo-1540541338287-41700207dee6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200"
          alt="Resort tropicale"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_60%_50%,_transparent_0%,_rgba(6,10,20,0.85)_100%)]" />
      </motion.div>

      <div className="aurora" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-12 bg-gold/60" />
          <span className="overline">Agenzia Viaggi · Premium Concierge</span>
        </motion.div>

        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tighter text-white max-w-5xl">
          {"Scopri.".split("").map((c, i) => (
            <motion.span key={`s-${i}`} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 + i * 0.03, duration: 0.8, ease: [0.2,0.8,0.2,1] }} className="inline-block">{c}</motion.span>
          ))}{" "}
          <span className="block italic"><span className="gold-text">Vivi.</span></span>
          <span className="block">Ricorda. <motion.span initial={{ scale:0, rotate:-30 }} animate={{ scale:1, rotate:0 }} transition={{ delay: 1.2, type: "spring" }} className="inline-block">✨</motion.span></span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
        >
          Pacchetti curati a mano, volo + hotel inclusi, assistenza dedicata. Dalle isole spagnole al Mar Rosso, dalle Cicladi alle Maldive. <span className="text-gold">Tu sogni, noi ci occupiamo del resto.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4 items-center"
        >
          <a data-testid="hero-cta-whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center gap-2">
            Prenota su WhatsApp <ArrowRight className="h-4 w-4" />
          </a>
          <a data-testid="hero-cta-packages" href="#pacchetti" className="btn-ghost-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center gap-2">
            Vedi i Pacchetti
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-16 flex flex-wrap items-center gap-6 text-xs text-white/50"
        >
          <div className="flex items-center gap-2"><Star className="h-4 w-4 text-gold" fill="#C9A961" /> <span>5.0 · clienti soddisfatti</span></div>
          <div className="h-4 w-px bg-white/15" />
          <div className="flex items-center gap-2"><Plane className="h-4 w-4 text-gold" /> Volo A/R sempre incluso</div>
          <div className="h-4 w-px bg-white/15" />
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Assicurazione inclusa</div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="overline text-[10px]">SCROLL</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

/* ---------------- Marquee Ticker ---------------- */
function Ticker() {
  const items = ["TENERIFE 🌋", "SHARM EL-SHEIKH 🐪", "IBIZA 🎧", "MYKONOS 🧿", "MARBELLA 🥂", "MADRID ⛪", "BARCELLONA 🏛️", "MIAMI 🛥️", "MALDIVE 🏝️"];
  return (
    <div className="relative border-y border-white/5 bg-ink-panel/40 py-6 marquee-mask overflow-hidden">
      <div className="flex animate-marquee whitespace-nowrap">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="mx-8 font-serif text-2xl md:text-3xl text-white/40 hover:text-gold transition-colors">
            {t} <span className="text-gold mx-3">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Trust Badges ---------------- */
function TrustBadges() {
  const items = [
    { icon: Plane, title: "Volo A/R Incluso", desc: "Sempre incluso nei nostri pacchetti", emoji: "✈️" },
    { icon: Hotel, title: "Hotel Selezionati", desc: "Strutture testate e di qualità", emoji: "🏨" },
    { icon: ShieldCheck, title: "Assicurazione", desc: "Volo + assistenza medica inclusa", emoji: "🛡️" },
    { icon: Headphones, title: "Assistenza 24/7", desc: "Siamo con te prima, durante e dopo", emoji: "🛎️" },
  ];
  return (
    <section id="perche" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 reveal-up">
            <div className="section-divider mb-6"><span className="overline">PERCHÉ NOI</span><div className="line" /></div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-white">
              Viaggi <span className="gold-text italic">premium</span>,<br/>zero pensieri.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Selezioniamo personalmente ogni struttura, ogni volo, ogni dettaglio. Tu scrivi, noi organizziamo tutto su WhatsApp.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {items.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                className="glass gold-border rounded-2xl p-6 card-lift"
                data-testid={`trust-card-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center text-2xl">
                    {it.emoji}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-white">{it.title}</h3>
                    <p className="mt-1 text-sm text-white/55 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Stats ---------------- */
function CountUp({ to, suffix = "" }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const dur = 1600;
        const start = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setV(Math.floor(eased * to));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{v.toLocaleString("it-IT")}{suffix}</span>;
}

function Stats() {
  const items = [
    { num: 5000, suf: "+", label: "Clienti felici", emoji: "🥂" },
    { num: 50, suf: "+", label: "Mete esclusive", emoji: "🌍" },
    { num: 24, suf: "/7", label: "Assistenza dedicata", emoji: "🛎️" },
    { num: 100, suf: "%", label: "Pacchetti curati", emoji: "✨" },
  ];
  return (
    <section className="relative py-20 border-y border-white/5 bg-ink-panel/30">
      <div className="aurora opacity-40" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 relative">
        {items.map((s, i) => (
          <div key={i} className="text-center reveal-up" data-testid={`stat-${i}`}>
            <div className="text-3xl mb-2">{s.emoji}</div>
            <div className="font-serif text-4xl md:text-5xl gold-text">
              <CountUp to={s.num} suffix={s.suf} />
            </div>
            <div className="mt-2 text-xs md:text-sm text-white/50 tracking-wider uppercase">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Destinations Bento ---------------- */
function Destinations({ packages }) {
  const sorted = [...packages].sort((a, b) => a.order - b.order).slice(0, 6);
  // Layout pattern: first big, others mixed
  const layout = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];
  return (
    <section id="mete" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal-up">
          <div>
            <div className="section-divider mb-6"><span className="overline">DESTINAZIONI</span><div className="line max-w-[120px]" /></div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-white">
              Mete del <em className="gold-text not-italic">momento</em> ✈️
            </h2>
          </div>
          <p className="text-white/55 max-w-md leading-relaxed">
            Le destinazioni più richieste del nostro profilo TikTok. Tocca per scoprire i dettagli e prenotare su WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 auto-rows-[260px] gap-4">
          {sorted.map((p, i) => (
            <motion.a
              key={p.id}
              href="#pacchetti"
              data-testid={`dest-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08 }}
              className={`group zoom-parent relative overflow-hidden rounded-2xl glass gold-border ${layout[i] || "lg:col-span-4"}`}
            >
              <img src={p.image} alt={p.destination} className="zoom-img absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />

              {p.badge && (
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] tracking-wider uppercase bg-gold text-ink font-semibold">
                  <Sparkles className="h-3 w-3" /> {p.badge}
                </div>
              )}

              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 z-10">
                <div className="overline mb-2 text-gold/80">{p.country} {p.flag}</div>
                <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight flex items-center gap-2">
                  {p.destination} <span className="text-xl">{p.emoji}</span>
                </h3>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-white/70 text-sm flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5" /> {p.nights}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-white/50 tracking-widest uppercase">A partire da</div>
                    <div className="gold-text font-serif text-2xl">{formatPrice(p.price)}</div>
                  </div>
                </div>
              </div>

              <div className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight className="h-4 w-4 text-white" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Packages full list ---------------- */
function Packages({ packages }) {
  const [filter, setFilter] = useState("all");
  const tags = [
    { id: "all", label: "Tutti", emoji: "🌍" },
    { id: "Bestseller", label: "Bestseller", emoji: "🔥" },
    { id: "Pasqua", label: "Pasqua", emoji: "🐣" },
    { id: "Premium", label: "Premium", emoji: "💎" },
    { id: "Luxury", label: "Luxury", emoji: "👑" },
  ];
  const list = filter === "all" ? packages : packages.filter((p) => (p.badge || "").includes(filter));

  return (
    <section id="pacchetti" className="relative py-24 md:py-32 bg-ink-deep/60">
      <div className="aurora opacity-50" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 reveal-up">
          <div>
            <div className="section-divider mb-6"><span className="overline">PACCHETTI</span><div className="line max-w-[120px]" /></div>
            <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
              Le nostre <em className="gold-text not-italic">offerte</em> 🌴
            </h2>
            <p className="mt-4 text-white/55 max-w-xl">
              Volo A/R + Hotel + Assicurazione. Tutto già incluso, tu pensa solo a partire.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t.id}
                data-testid={`pkg-filter-${t.id}`}
                onClick={() => setFilter(t.id)}
                className={`px-4 py-2 rounded-full text-xs tracking-wider uppercase transition-all border ${
                  filter === t.id
                    ? "bg-gold text-ink border-gold"
                    : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
                }`}
              >
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => (
              <motion.article
                key={p.id}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                className="group zoom-parent glass gold-border rounded-2xl overflow-hidden card-lift"
                data-testid={`pkg-card-${p.destination.toLowerCase()}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={p.image} alt={p.destination} className="zoom-img absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  {p.badge && (
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] tracking-wider uppercase bg-gold text-ink font-semibold">
                      <Sparkles className="h-3 w-3" /> {p.badge}
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4">
                    <div className="overline text-gold/90">{p.country} {p.flag}</div>
                    <h3 className="font-serif text-2xl text-white">{p.destination} <span>{p.emoji}</span></h3>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-sm text-white/60 leading-relaxed min-h-[48px]">{p.description}</p>

                  <div className="mt-4 flex items-center gap-4 text-xs text-white/50">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-gold" /> {p.nights}</span>
                    {p.period && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-gold" /> {p.period}</span>}
                  </div>

                  <ul className="mt-4 space-y-1.5">
                    {p.includes?.slice(0, 4).map((inc, idx) => (
                      <li key={idx} className="text-xs text-white/65 flex gap-2 items-start">
                        <CheckCircle2 className="h-3.5 w-3.5 text-gold mt-0.5 shrink-0" /> <span>{inc}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="hairline my-5" />

                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-widest">A partire da</div>
                      <div className="font-serif text-3xl gold-text">{formatPrice(p.price)}</div>
                      <div className="text-[11px] text-white/40">a persona</div>
                    </div>
                    <a
                      href={waLink(waPackageMessage(p))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`pkg-cta-${p.destination.toLowerCase()}`}
                      className="btn-gold rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-1.5"
                    >
                      Prenota <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {list.length === 0 && (
          <div className="text-center py-20 text-white/40">Nessun pacchetto in questa categoria.</div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
function Testimonials() {
  const items = [
    { name: "Giulia M.", from: "Milano", text: "Tenerife organizzato in 24h via WhatsApp. Hotel pazzesco, tutto come promesso. Tornerò sicuramente!", rating: 5, dest: "Tenerife 🌋" },
    { name: "Marco R.", from: "Roma", text: "Mai sentito un'agenzia così disponibile. Sharm in pensione completa a meno di 250€... incredibile.", rating: 5, dest: "Sharm 🐪" },
    { name: "Sara L.", from: "Napoli", text: "Mykonos da sogno. Hotel selezionato benissimo, transfer puntualissimi. Vi adoro!", rating: 5, dest: "Mykonos 🧿" },
    { name: "Alessandro B.", from: "Torino", text: "Ibiza con gli amici, organizzato in 2 giorni. Prezzo top, assistenza sempre attiva.", rating: 5, dest: "Ibiza 🎧" },
    { name: "Chiara F.", from: "Firenze", text: "Pasqua a Barcellona a 149€?! Sembrava troppo bello, invece tutto perfetto. Consigliatissimo.", rating: 5, dest: "Barcellona 🏛️" },
  ];
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal-up">
          <div className="section-divider mb-6 justify-center"><div className="line" /><span className="overline">RECENSIONI</span><div className="line" /></div>
          <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            La parola ai <em className="gold-text not-italic">nostri viaggiatori</em> 💬
          </h2>
        </div>

        <div className="marquee-mask overflow-hidden">
          <div className="flex animate-marquee-slow gap-6 py-4">
            {[...items, ...items].map((t, i) => (
              <article
                key={i}
                className="glass gold-border rounded-2xl p-6 md:p-7 w-[340px] md:w-[400px] shrink-0"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-6 w-6 text-gold/60 mb-3" />
                <p className="text-white/80 leading-relaxed text-sm">{t.text}</p>
                <div className="hairline my-4" />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">{t.name}</div>
                    <div className="text-xs text-white/50">{t.from} · {t.dest}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="h-3.5 w-3.5 text-gold" fill="#C9A961" />
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FAQ() {
  const faqs = [
    { q: "Come posso prenotare un pacchetto?", a: "Tutte le prenotazioni avvengono tramite WhatsApp al +39 350 897 5700. Cliccando su 'Prenota' sui pacchetti ti si aprirà direttamente la chat con il messaggio precompilato. 💬" },
    { q: "Cosa include il prezzo?", a: "Volo A/R, hotel selezionato, assicurazione volo + assistenza medica, bagaglio (come specificato nel pacchetto) e tasse aeroportuali. Per i pacchetti Sharm anche pensione completa e transfer." },
    { q: "Posso modificare le date del pacchetto?", a: "Sì! I periodi indicati sono le proposte più richieste, ma su WhatsApp possiamo personalizzare il pacchetto in base alle tue esigenze. ✈️" },
    { q: "Quali sono i metodi di pagamento?", a: "Bonifico bancario, carta o pagamento rateale (a seconda dell'importo). Ne parliamo direttamente in chat per trovare la soluzione migliore per te." },
    { q: "C'è assistenza durante il viaggio?", a: "Sì, assistenza 24/7 via WhatsApp prima, durante e dopo il viaggio. 🛎️" },
    { q: "I prezzi sono a persona?", a: "Sì, tutti i prezzi sono indicati 'a persona' in camera doppia. Per camere singole o supplementi ti facciamo un preventivo dedicato." },
  ];
  return (
    <section id="faq" className="relative py-24 md:py-32 bg-ink-panel/30 border-y border-white/5">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14 reveal-up">
          <div className="section-divider mb-6 justify-center"><div className="line" /><span className="overline">DOMANDE FREQUENTI</span><div className="line" /></div>
          <h2 className="font-serif text-4xl md:text-5xl text-white">Tutto chiaro? <em className="gold-text not-italic">Quasi.</em> ❓</h2>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-white hover:text-gold text-base md:text-lg font-medium py-5">
                <span className="flex items-center gap-3">
                  <span className="gold-text font-mono text-sm">0{i + 1}</span> {f.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-sm md:text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */
function Contact() {
  return (
    <section id="contatti" className="relative py-24 md:py-32 overflow-hidden">
      <div className="aurora opacity-60" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal-up">
            <div className="section-divider mb-6"><span className="overline">CONTATTI</span><div className="line max-w-[120px]" /></div>
            <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
              Il tuo prossimo viaggio <br />
              <em className="gold-text not-italic">inizia qui</em> 🌍
            </h2>
            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              Scrivici su WhatsApp con la destinazione che hai in mente. Ti rispondiamo personalmente con un preventivo dedicato.
            </p>

            <div className="mt-10 space-y-4">
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-wa" className="glass gold-border rounded-2xl p-5 flex items-center gap-4 card-lift">
                <div className="h-12 w-12 rounded-full bg-[#25D366]/20 border border-[#25D366]/50 flex items-center justify-center text-2xl">💬</div>
                <div className="flex-1">
                  <div className="overline text-[#25D366]">WHATSAPP</div>
                  <div className="text-white text-lg font-serif">{WA_DISPLAY}</div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-gold" />
              </a>

              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok" className="glass gold-border rounded-2xl p-5 flex items-center gap-4 card-lift">
                <div className="h-12 w-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-2xl">🎵</div>
                <div className="flex-1">
                  <div className="overline">TIKTOK</div>
                  <div className="text-white text-lg font-serif">@atlante.viaggi</div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-gold" />
              </a>
            </div>
          </div>

          {/* Big CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative glass gold-border rounded-3xl p-8 md:p-12 overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-gold/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="relative">
              <div className="text-5xl mb-4 animate-float-y inline-block">✈️</div>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                Pronto a <span className="gold-text italic">decollare?</span>
              </h3>
              <p className="mt-4 text-white/60">
                Un click e sei in chat con noi. Dicci dove vuoi andare, ci pensiamo a tutto.
              </p>

              <div className="mt-8 flex flex-col gap-3">
                <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-cta-main" className="btn-gold rounded-full px-6 py-4 text-base text-center inline-flex items-center justify-center gap-2">
                  Apri chat WhatsApp <ArrowRight className="h-5 w-5" />
                </a>
                <a href="#pacchetti" className="btn-ghost-gold rounded-full px-6 py-4 text-sm text-center">
                  Vedi prima i pacchetti
                </a>
              </div>

              <div className="mt-8 flex items-center gap-2 text-xs text-white/45">
                <Heart className="h-3.5 w-3.5 text-gold" />
                <span>Risposta media in meno di 30 minuti</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  return (
    <footer className="relative bg-ink-deep border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Atlante Viaggi" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-serif text-2xl text-white">Atlante <span className="gold-text">Viaggi</span></div>
                <div className="overline text-[10px]">SCOPRI · VIVI · RICORDA</div>
              </div>
            </div>
            <p className="mt-5 text-white/55 text-sm leading-relaxed max-w-sm">
              Agenzia di viaggi premium. Pacchetti curati a mano, assistenza dedicata, esperienze indimenticabili.
            </p>
          </div>

          <div>
            <div className="overline mb-4">NAVIGAZIONE</div>
            <ul className="space-y-2 text-white/60 text-sm">
              <li><a href="#mete" className="hover:text-gold transition-colors">Mete</a></li>
              <li><a href="#pacchetti" className="hover:text-gold transition-colors">Pacchetti</a></li>
              <li><a href="#perche" className="hover:text-gold transition-colors">Perché Noi</a></li>
              <li><a href="#faq" className="hover:text-gold transition-colors">FAQ</a></li>
              <li><a href="#contatti" className="hover:text-gold transition-colors">Contatti</a></li>
            </ul>
          </div>

          <div>
            <div className="overline mb-4">CONTATTI</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-white hover:text-gold transition-colors inline-flex items-center gap-2">
                  💬 {WA_DISPLAY}
                </a>
              </li>
              <li>
                <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-gold transition-colors inline-flex items-center gap-2">
                  🎵 @atlante.viaggi
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="hairline mb-6" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Atlante Viaggi · Tutti i diritti riservati</div>
          <div className="font-serif italic text-gold/60">&ldquo;Il mondo è un libro, e chi non viaggia ne legge solo una pagina.&rdquo;</div>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Page ---------------- */
export default function Landing() {
  const packages = PACKAGES;

  return (
    <div className="relative bg-ink text-white">
      <Nav />
      <Hero />
      <Ticker />
      <TrustBadges />
      <Destinations packages={packages} />
      <Stats />
      <Packages packages={packages} />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
